/**
 * Share Secret — flujo REAL completo contra backend (BHM-CDX-DIR-083 §6).
 *
 * Contratos reales (@Controller('work'), área 'share'):
 *   POST /work/share-secret/resolve-user   {email} → user DIRECTO {id,email,displayName,status,publicKeyX25519,publicKeyEd25519}
 *   POST /work/share-secret/list-identities {email} → ShareIdentity[]
 *   POST /work/share-secret/select-identity {identityId,typedEmail} → ShareIdentity (valida email↔identidad)
 *   POST /work/share-secret  ShareSecretFlowInput {itemId,email,identityId,permissions,expiresAt?,itemKeyEncForRecipient(48B),wrappedKeyNonce(24B),maxUses?}
 *   GET  /work/share-secret/:id/status → {status,permissions,...}
 *   POST /work/share-secret/:id/revoke → {id,status:'REVOKED',revokedAt}
 *
 * Permisos válidos (SHARE_PERMISSION_KEYS): read, copy, edit, use, share, reshare, delete.
 * Cripto per-ítem (utils/item-dek.ts): DEK independiente por ítem; preparación
 * client-side solo la primera vez que un ítem legacy se comparte. El servidor
 * solo persiste ciphertext y sobre (itemKeyEncForRecipient + wrappedKeyNonce).
 */
import { useCallback, useEffect, useState } from 'react';
import { api } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { wrapDekForRecipient } from '../utils/sharing';
import { itemEncryptionKey, toBytes } from '../utils/item-dek';
import { decryptField } from '../stores/vaultStore';

const SHARE_PERMISSIONS = ['read', 'copy', 'edit', 'use', 'share', 'reshare', 'delete'] as const;
type SharePermission = (typeof SHARE_PERMISSIONS)[number];

interface ShareIdentity {
  id: string;
  kind: 'USER' | 'EXTERNAL';
  email: string;
  displayName?: string | null;
  provider?: string;
}

interface SecretOption {
  itemId: string;
  vaultId: string;
  name: string;
}

type Step = 'SECRET' | 'EMAIL' | 'RESOLVED' | 'SELECTED' | 'SHARING' | 'SUCCESS' | 'ERROR';

export function ShareSecretPanel({ itemId: initialItemId }: { itemId?: string } = {}) {
  const masterKey = useAuthStore((s) => s.masterKey);

  const [secrets, setSecrets] = useState<SecretOption[]>([]);
  const [itemId, setItemId] = useState<string>(initialItemId && initialItemId !== 'placeholder' ? initialItemId : '');
  const [step, setStep] = useState<Step>('SECRET');
  const [email, setEmail] = useState('');
  const [resolved, setResolved] = useState<{ id: string; email: string; displayName: string | null } | null>(null);
  const [identities, setIdentities] = useState<ShareIdentity[]>([]);
  const [identity, setIdentity] = useState<ShareIdentity | null>(null);
  const [permissions, setPermissions] = useState<Record<SharePermission, boolean>>(
    { read: true, copy: false, edit: false, use: false, share: false, reshare: false, delete: false },
  );
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [lastShare, setLastShare] = useState<{ id: string; status: string } | null>(null);
  const [busy, setBusy] = useState(false);

  /** Selector de secretos REALES accesibles (GET /vaults + GET /vaults/:id/items). */
  const loadSecrets = useCallback(async () => {
    if (!masterKey) return;
    try {
      const { data: vaults } = await api.get('/vaults');
      const list: SecretOption[] = [];
      for (const v of Array.isArray(vaults) ? vaults : []) {
        const { data } = await api.get(`/vaults/${v.id}/items`);
        const items = Array.isArray(data) ? data : (data?.items ?? []);
        for (const raw of items as Record<string, unknown>[]) {
          const name = await decryptField(raw.nameEnc, masterKey).catch(() => '');
          list.push({ itemId: String(raw.id), vaultId: String(raw.vaultId ?? v.id), name: name || '[cifrado]' });
        }
      }
      setSecrets(list);
      if (!itemId && list.length > 0 && initialItemId && initialItemId !== 'placeholder') setItemId(initialItemId);
    } catch {
      setSecrets([]);
    }
  }, [masterKey, itemId, initialItemId]);

  useEffect(() => {
    void loadSecrets();
  }, [loadSecrets]);

  const fail = (e: unknown, fallback: string) => {
    const err = e as { response?: { data?: { message?: string } }; message?: string };
    setError(err?.response?.data?.message || err?.message || fallback);
    setStep('ERROR');
    setBusy(false);
  };

  /** 1. resolve-user → respuesta PLANA (usuario directo, NO res.identity). */
  const resolveUser = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/work/share-secret/resolve-user', { email });
      const user = res.data as { id: string; email: string; displayName: string | null };
      setResolved(user);
      // 2. list-identities → mostrar identidad real para selección explícita
      const ids = await api.post('/work/share-secret/list-identities', { email });
      setIdentities(Array.isArray(ids.data) ? ids.data : [ids.data].filter(Boolean));
      setStep('RESOLVED');
      setBusy(false);
    } catch (e: unknown) {
      fail(e, 'Usuario no encontrado');
    }
  };

  /** 3. select-identity → validación real email escrito ↔ identidad seleccionada. */
  const chooseIdentity = async (id: ShareIdentity) => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/work/share-secret/select-identity', { identityId: id.id, typedEmail: email });
      setIdentity(res.data as ShareIdentity);
      setStep('SELECTED');
      setBusy(false);
    } catch (e: unknown) {
      fail(e, 'La identidad seleccionada no coincide con el email escrito');
    }
  };

  /** Prepara la DEK por-ítem (migración client-side única) y la envuelve para el destinatario. */
  const prepareAndWrap = async (targetItemId: string, recipientId: string) => {
    if (!masterKey) throw new Error('Desbloquea el proyecto para compartir secretos');
    const { data: item } = await api.get(`/vaults/items/${targetItemId}`);
    const { key, legacy, dek } = await itemEncryptionKey(masterKey, targetItemId, item.nameEnc);

    if (legacy) {
      // Migración client-side: re-cifrar campos del ítem con su DEK (una sola vez).
      const reencrypt = async (enc: unknown): Promise<number[] | undefined> => {
        const packed = toBytes(enc);
        if (packed.length <= 40) return undefined;
        const plaintext = await decryptField(enc, key);
        const { encrypt } = await import('@barmetvault/crypto');
        const result = encrypt(new TextEncoder().encode(plaintext), dek);
        return Array.from(new Uint8Array([...result.nonce, ...result.ciphertext]));
      };
      const body: Record<string, unknown> = {};
      for (const field of ['nameEnc', 'usernameEnc', 'passwordEnc', 'urlsEnc', 'notesEnc', 'totpSecretEnc', 'customFieldsEnc'] as const) {
        const reenc = await reencrypt(item[field]);
        if (reenc) body[field] = reenc;
      }
      await api.put(`/vaults/items/${targetItemId}`, body);
    }

    const wrapped = await wrapDekForRecipient(dek, recipientId);
    return wrapped;
  };

  /** 4-7. share → contrato ShareSecretFlowInput real. */
  const share = async () => {
    if (!itemId || !identity || !resolved) return;
    setBusy(true);
    setError(null);
    setStep('SHARING');
    try {
      const { itemKeyEncForRecipient, wrappedKeyNonce } = await prepareAndWrap(itemId, resolved.id);
      const activePermissions: Record<string, boolean> = {};
      for (const p of SHARE_PERMISSIONS) if (permissions[p]) activePermissions[p] = true;

      const res = await api.post('/work/share-secret', {
        itemId,
        email,
        identityId: identity.id,
        permissions: activePermissions,
        expiresAt: expiresAt || undefined,
        itemKeyEncForRecipient,
        wrappedKeyNonce,
      });
      const share = res.data as { id: string; status: string };
      // 8. confirmación con estado REAL del servidor
      const status = await api.get(`/work/share-secret/${share.id}/status`);
      setLastShare({ id: share.id, status: (status.data as { status: string }).status ?? share.status });
      setStep('SUCCESS');
      setBusy(false);
    } catch (e: unknown) {
      fail(e, 'No se pudo compartir el secreto');
    }
  };

  /** 9. revoke — revocación explícita. */
  const revoke = async () => {
    if (!lastShare) return;
    setBusy(true);
    try {
      const res = await api.post(`/work/share-secret/${lastShare.id}/revoke`);
      setLastShare({ id: lastShare.id, status: (res.data as { status: string }).status ?? 'REVOKED' });
      setBusy(false);
    } catch (e: unknown) {
      fail(e, 'No se pudo revocar el acceso');
    }
  };

  return (
    <div className="share-secret-panel" data-testid="share-secret-panel">
      <h2>Compartir secreto</h2>

      {step === 'SECRET' && (
        <div data-testid="share-secret-picker">
          <label htmlFor="share-secret-select">Secreto a compartir</label>
          <select
            id="share-secret-select" data-testid="share-secret-select"
            value={itemId} onChange={(e) => setItemId(e.target.value)}
          >
            <option value="">Selecciona un secreto…</option>
            {secrets.map((s) => (
              <option key={s.itemId} value={s.itemId}>{s.name}</option>
            ))}
          </select>
          <button
            data-testid="share-secret-confirm"
            disabled={!itemId}
            onClick={() => setStep('EMAIL')}
          >
            Continuar
          </button>
        </div>
      )}

      {step === 'EMAIL' && (
        <div>
          <label htmlFor="share-email">Email del destinatario</label>
          <input
            id="share-email" type="email" data-testid="share-email"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="usuario@ejemplo.com"
          />
          <button data-testid="share-resolve-btn" onClick={() => void resolveUser()} disabled={!email || busy}>
            Resolver usuario
          </button>
          <button data-testid="share-back-secret" onClick={() => setStep('SECRET')}>
            Volver
          </button>
        </div>
      )}

      {step === 'RESOLVED' && resolved && (
        <div data-testid="share-identity-found">
          <p>Usuario resuelto: <strong>{resolved.displayName || resolved.email}</strong> ({resolved.email})</p>
          <p>Identidades disponibles — selecciona explícitamente:</p>
          <ul>
            {(identities.length ? identities : [{ id: resolved.id, kind: 'USER' as const, email: resolved.email }]).map((id) => (
              <li key={id.id}>
                <button
                  data-testid="share-identity-option"
                  onClick={() => void chooseIdentity(id)}
                  disabled={busy}
                >
                  {id.kind}: {id.email} {id.displayName ? `(${id.displayName})` : ''}
                </button>
              </li>
            ))}
          </ul>
          <button data-testid="share-cancel-btn" onClick={() => setStep('EMAIL')}>
            Cancelar
          </button>
        </div>
      )}

      {step === 'SELECTED' && identity && (
        <div data-testid="share-permissions-form">
          <p>Compartir con: <strong>{identity.email}</strong> ({identity.kind})</p>
          <fieldset>
            <legend>Permisos</legend>
            {SHARE_PERMISSIONS.map((p) => (
              <label key={p}>
                <input
                  type="checkbox" checked={permissions[p]}
                  onChange={(e) => setPermissions((s) => ({ ...s, [p]: e.target.checked }))}
                />
                {p}
              </label>
            ))}
          </fieldset>
          <div>
            <label htmlFor="share-expiry">Caducidad (opcional)</label>
            <input
              id="share-expiry" type="datetime-local" data-testid="share-expiry"
              value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
          <button
            data-testid="share-confirm-btn" onClick={() => void share()}
            disabled={busy || !Object.values(permissions).some(Boolean)}
          >
            Confirmar compartir
          </button>
        </div>
      )}

      {step === 'SHARING' && (
        <div data-testid="share-loading" role="status">Compartiendo…</div>
      )}

      {step === 'SUCCESS' && (
        <div>
          <div data-testid="share-success" role="alert" className="success">
            Secreto compartido correctamente con {identity?.email} · estado: {lastShare?.status}
          </div>
          {lastShare && lastShare.status !== 'REVOKED' && (
            <button data-testid="share-revoke-btn" onClick={() => void revoke()} disabled={busy}>
              Revocar acceso
            </button>
          )}
          {lastShare?.status === 'REVOKED' && (
            <div data-testid="share-revoked" role="status">Acceso revocado</div>
          )}
          <button data-testid="share-again-btn" onClick={() => { setStep('SECRET'); setLastShare(null); }}>
            Compartir otro secreto
          </button>
        </div>
      )}

      {step === 'ERROR' && (
        <div data-testid="share-error" role="alert" className="error">
          {error}
          <button data-testid="share-retry-btn" onClick={() => { setError(null); setStep('SECRET'); }}>
            Reintentar
          </button>
        </div>
      )}
    </div>
  );
}
